import React, { useState, useRef, useEffect } from 'react';

export default function PinLock({ onLogin, error, loading }) {
  const [pass, setPass] = useState('');
  const inputRef = useRef(null);

  useEffect(() => { inputRef.current?.focus(); }, []);

  const submit = (e) => {
    e.preventDefault();
    if (pass.trim() && !loading) onLogin(pass);
  };

  return (
    <div style={s.overlay}>
      {/* Animated mesh background */}
      <div style={s.meshBg} />

      <div style={s.card}>
        {/* Logo */}
        <div style={s.logoWrap}>
          <div style={s.logoBadge}>
            <svg width="28" height="28" viewBox="0 0 24 24" fill="none">
              <path d="M3 12h3l2-7 4 14 3-10 2 3h4" stroke="#6366f1" strokeWidth="2.5"
                strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </div>
        </div>

        <h1 style={s.title}>SmartPatch</h1>
        <p style={s.subtitle}>Athlete Biometric Dashboard</p>

        {/* Lock icon */}
        <div style={s.lockCircle}>
          <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="#6366f1" strokeWidth="2" strokeLinecap="round">
            <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/>
            <path d="M7 11V7a5 5 0 0 1 10 0v4"/>
          </svg>
        </div>

        <p style={s.prompt}>Enter your passphrase to access live health data</p>

        {/* Form */}
        <form onSubmit={submit} style={s.form}>
          <div style={s.inputWrap}>
            <input
              ref={inputRef}
              type="password"
              value={pass}
              onChange={e => setPass(e.target.value)}
              placeholder="Passphrase"
              style={{
                ...s.input,
                borderColor: error ? '#fca5a5' : pass ? '#a5b4fc' : '#e5e7eb',
                boxShadow  : error ? '0 0 0 3px rgba(239,68,68,0.12)' : pass ? '0 0 0 3px rgba(99,102,241,0.12)' : 'none',
              }}
              disabled={loading}
              autoComplete="current-password"
            />
            {pass && !loading && (
              <button type="button" style={s.clearBtn} onClick={() => setPass('')} tabIndex={-1}>✕</button>
            )}
          </div>

          {/* Error message */}
          {error && (
            <div style={s.errorBox}>
              <span style={s.errorIcon}>⚠</span>
              {error}
            </div>
          )}

          <button
            type="submit"
            disabled={!pass.trim() || loading}
            style={{
              ...s.submitBtn,
              opacity: !pass.trim() || loading ? 0.55 : 1,
              cursor : !pass.trim() || loading ? 'not-allowed' : 'pointer',
            }}
          >
            {loading
              ? <><span style={s.spinner} />Verifying…</>
              : <><span>Unlock Dashboard</span><span style={s.arrow}>→</span></>
            }
          </button>
        </form>

        {/* Footer hint */}
        <div style={s.hint}>
          <span style={s.hintDot} />
          Session auto-locks after 5 minutes of inactivity
        </div>

        {/* Security badge */}
        <div style={s.secBadge}>
          <svg width="11" height="11" viewBox="0 0 24 24" fill="#10b981">
            <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
          </svg>
          SHA-256 protected · No data stored
        </div>
      </div>
    </div>
  );
}

const s = {
  overlay: {
    position       : 'fixed',
    inset          : 0,
    display        : 'flex',
    alignItems     : 'center',
    justifyContent : 'center',
    padding        : 20,
    zIndex         : 9999,
    background     : '#eef2ff',
    backgroundImage: `
      radial-gradient(ellipse at 20% 10%, #c7d2fe 0%, transparent 50%),
      radial-gradient(ellipse at 80% 80%, #ddd6fe 0%, transparent 50%),
      radial-gradient(ellipse at 60% 20%, #bfdbfe 0%, transparent 40%)`,
    animation: 'fade-in .3s ease',
  },
  meshBg: { position: 'absolute', inset: 0, backdropFilter: 'blur(40px)' },
  card: {
    position        : 'relative',
    width           : '100%',
    maxWidth        : 400,
    background      : 'rgba(255,255,255,0.82)',
    backdropFilter  : 'blur(24px)',
    WebkitBackdropFilter: 'blur(24px)',
    borderRadius    : 24,
    border          : '1px solid rgba(255,255,255,0.95)',
    boxShadow       : '0 24px 64px rgba(99,102,241,0.18), 0 1px 0 rgba(255,255,255,0.9) inset',
    padding         : '40px 36px 32px',
    display         : 'flex',
    flexDirection   : 'column',
    alignItems      : 'center',
    gap             : 10,
  },
  logoWrap  : { marginBottom: 4 },
  logoBadge : {
    width       : 60,
    height      : 60,
    borderRadius: 18,
    background  : 'linear-gradient(135deg, #e0e7ff, #eef2ff)',
    border      : '1.5px solid #c7d2fe',
    display     : 'flex',
    alignItems  : 'center',
    justifyContent: 'center',
    boxShadow   : '0 4px 16px rgba(99,102,241,0.2)',
  },
  title    : { fontSize: 24, fontWeight: 800, color: '#1e1b4b', letterSpacing: '-0.5px' },
  subtitle : { fontSize: 13, color: '#6b7280', fontWeight: 500, marginTop: -4 },
  lockCircle: {
    width       : 56,
    height      : 56,
    borderRadius: '50%',
    background  : 'linear-gradient(135deg, #eef2ff, #e0e7ff)',
    border      : '1.5px solid #c7d2fe',
    display     : 'flex',
    alignItems  : 'center',
    justifyContent: 'center',
    margin      : '6px 0',
  },
  prompt   : { fontSize: 13, color: '#6b7280', textAlign: 'center', lineHeight: 1.5, fontWeight: 500, maxWidth: 280 },
  form     : { width: '100%', display: 'flex', flexDirection: 'column', gap: 10, marginTop: 6 },
  inputWrap: { position: 'relative' },
  input    : {
    width       : '100%',
    padding     : '13px 42px 13px 16px',
    fontSize    : 15,
    fontFamily  : 'Inter, sans-serif',
    fontWeight  : 500,
    color       : '#1e1b4b',
    background  : 'rgba(255,255,255,0.9)',
    border      : '1.5px solid',
    borderRadius: 12,
    outline     : 'none',
    transition  : 'border-color .15s, box-shadow .15s',
    letterSpacing: '0.1em',
  },
  clearBtn : {
    position   : 'absolute',
    right      : 12,
    top        : '50%',
    transform  : 'translateY(-50%)',
    background : 'none',
    border     : 'none',
    cursor     : 'pointer',
    color      : '#9ca3af',
    fontSize   : 13,
    padding    : 4,
  },
  errorBox : {
    display     : 'flex',
    alignItems  : 'center',
    gap         : 7,
    background  : '#fff1f2',
    border      : '1px solid #fecaca',
    borderRadius: 10,
    padding     : '10px 14px',
    fontSize    : 13,
    color       : '#dc2626',
    fontWeight  : 500,
  },
  errorIcon: { fontSize: 14 },
  submitBtn: {
    display        : 'flex',
    alignItems     : 'center',
    justifyContent : 'center',
    gap            : 8,
    padding        : '13px',
    background     : 'linear-gradient(135deg, #6366f1, #4f46e5)',
    color          : '#fff',
    border         : 'none',
    borderRadius   : 12,
    fontSize       : 15,
    fontWeight     : 700,
    fontFamily     : 'Inter, sans-serif',
    transition     : 'opacity .15s, transform .1s',
    boxShadow      : '0 4px 16px rgba(99,102,241,0.35)',
    width          : '100%',
  },
  arrow  : { fontSize: 16 },
  spinner: {
    display     : 'inline-block',
    width       : 16,
    height      : 16,
    border      : '2px solid rgba(255,255,255,0.4)',
    borderTopColor: '#fff',
    borderRadius: '50%',
    animation   : 'spin-slow .7s linear infinite',
  },
  hint: {
    display    : 'flex',
    alignItems : 'center',
    gap        : 6,
    fontSize   : 11,
    color      : '#9ca3af',
    fontWeight : 500,
    marginTop  : 4,
  },
  hintDot : { width: 5, height: 5, borderRadius: '50%', background: '#c7d2fe', flexShrink: 0 },
  secBadge: {
    display    : 'flex',
    alignItems : 'center',
    gap        : 5,
    fontSize   : 11,
    color      : '#059669',
    fontWeight : 600,
    padding    : '5px 12px',
    background : '#ecfdf5',
    border     : '1px solid #a7f3d0',
    borderRadius: 99,
  },
};
